import React, { useState } from 'react';
import {
  Map,
  Building2,
  Users,
  ShieldAlert,
  FileText,
  MapPin,
  Compass,
  Sparkles,
  Phone,
  Mail,
} from 'lucide-react';

export const AdminProjectInfoView: React.FC = () => {
  const [activeSubTab, setActiveSubTab] = useState<'info' | 'board' | 'bylaws'>('info');

  const committeeMembers = [
    {
      name: 'นายแพทย์ ทรงพล ธนาเจริญกุล',
      role: 'ประธานกรรมการนิติบุคคล',
      house: '88/01 (ซอย 1)',
      phone: '081-889-1234',
      term: '2568 - 2570',
    },
    {
      name: 'คุณวิมลรัตน์ พงษ์ไพศาล',
      role: 'รองประธานกรรมการ & การเงิน',
      house: '88/18 (ซอย 2)',
      phone: '089-445-6789',
      term: '2568 - 2570',
    },
    {
      name: 'ว่าที่ ร.ต. อัครเดช เกียรติคุณ',
      role: 'กรรมการฝ่ายรักษาความปลอดภัย & ช่าง',
      house: '88/45 (ซอย 4)',
      phone: '086-332-9900',
      term: '2568 - 2570',
    },
    {
      name: 'ดร. นันทนา สุริยวรรณ',
      role: 'กรรมการฝ่ายสิ่งแวดล้อม & ภูมิทัศน์',
      house: '88/62 (ซอย 5)',
      phone: '084-556-2211',
      term: '2568 - 2570',
    },
    {
      name: 'คุณศศิธร สุขสมบูรณ์',
      role: 'ผู้จัดการนิติบุคคล (Juristic Manager)',
      house: 'สำนักงานนิติบุคคล',
      phone: '035-998-112',
      term: 'ประจำการ',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="p-2 rounded-xl bg-indigo-50 text-indigo-600 border border-indigo-200">
            <Map className="w-5 h-5" />
          </div>
          <div>
            <h2 className="text-base sm:text-lg font-bold text-slate-900">
              9. ข้อมูลโครงการและคณะกรรมการนิติบุคคล (Project Info & Master Plan)
            </h2>
            <p className="text-xs text-slate-500">
              โครงการ นราวดี อิลิแกนซ์ (อยุธยา) • ทะเบียนนิติบุคคลเลขที่ อย.0024/น.บ.042/2568
            </p>
          </div>
        </div>

        {/* Tab selector */}
        <div className="flex bg-slate-100 p-1 rounded-xl text-xs font-semibold text-slate-600">
          <button
            onClick={() => setActiveSubTab('info')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeSubTab === 'info' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
            }`}
          >
            ผังโครงการ & ข้อมูลทั่วไป
          </button>
          <button
            onClick={() => setActiveSubTab('board')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeSubTab === 'board' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
            }`}
          >
            คณะกรรมการนิติบุคคล
          </button>
          <button
            onClick={() => setActiveSubTab('bylaws')}
            className={`px-3 py-1.5 rounded-lg transition-all ${
              activeSubTab === 'bylaws' ? 'bg-white text-blue-700 shadow-xs' : 'hover:text-slate-900'
            }`}
          >
            ข้อบังคับนิติบุคคล
          </button>
        </div>
      </div>

      {activeSubTab === 'info' && (
        <div className="space-y-6">
          {/* Project Highlights */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
              <span className="text-xs text-slate-500">จำนวนบ้านทั้งหมด</span>
              <p className="text-2xl font-bold text-slate-900 font-mono">142 ยูนิต</p>
              <p className="text-[11px] text-slate-400">แบบบ้าน Grandeur & Premier</p>
            </div>

            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
              <span className="text-xs text-slate-500">ขนาดพื้นที่โครงการ</span>
              <p className="text-2xl font-bold text-blue-700 font-mono">48 ไร่ 2 งาน</p>
              <p className="text-[11px] text-blue-600">พื้นที่ส่วนกลางกว่า 4 ไร่</p>
            </div>

            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
              <span className="text-xs text-slate-500">ซอยทั้งหมดในโครงการ</span>
              <p className="text-2xl font-bold text-emerald-700 font-mono">8 ซอย</p>
              <p className="text-[11px] text-emerald-600">ถนนเมนกว้าง 16 ม. / ซอย 9 ม.</p>
            </div>

            <div className="p-4 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-1">
              <span className="text-xs text-slate-500">อัตราค่าส่วนกลาง</span>
              <p className="text-2xl font-bold text-purple-700 font-mono">35 บาท/ตร.ว.</p>
              <p className="text-[11px] text-purple-600">ชำระทุกรอบวันที่ 1 ของเดือน</p>
            </div>
          </div>

          {/* Master Plan Zone Layout */}
          <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="font-bold text-slate-900 text-base">ผังแม่บทโครงการและโซนซอย (Master Plan)</h3>
                <p className="text-xs text-slate-500">แผนผังการจัดสรรแปลงที่ดินและสิ่งอำนวยความสะดวก</p>
              </div>
              <span className="px-3 py-1 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-full text-xs font-bold">
                100% ก่อสร้างเสร็จสมบูรณ์
              </span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 pt-2">
              {[
                { zone: 'โซน A (ซอย 1 - 2)', units: '36 ยูนิต', desc: 'ใกล้ป้อม รปภ. หลัก ซุ้มประตูหรู', color: 'border-blue-200 bg-blue-50/40' },
                { zone: 'โซน B (ซอย 3 - 4)', units: '42 ยูนิต', desc: 'ตรงข้ามสโมสรคลับเฮาส์และสระว่ายน้ำ', color: 'border-emerald-200 bg-emerald-50/40' },
                { zone: 'โซน C (ซอย 5 - 6)', units: '38 ยูนิต', desc: 'ติดสวนสาธารณะส่วนกลางและสนามเด็กเล่น', color: 'border-purple-200 bg-purple-50/40' },
                { zone: 'โซน D (ซอย 7 - 8)', units: '26 ยูนิต', desc: 'โซนด้านใน แปลงริมสวน บรรยากาศเงียบสงบ', color: 'border-amber-200 bg-amber-50/40' },
              ].map((z, idx) => (
                <div key={idx} className={`p-4 rounded-xl border ${z.color} space-y-1`}>
                  <div className="flex justify-between items-center">
                    <h4 className="font-bold text-slate-900 text-xs">{z.zone}</h4>
                    <span className="text-[10px] font-bold text-slate-600 font-mono">{z.units}</span>
                  </div>
                  <p className="text-[11px] text-slate-600">{z.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeSubTab === 'board' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-bold text-slate-900 text-base">ทำเนียบคณะกรรมการนิติบุคคลชุดปัจจุบัน</h3>
              <p className="text-xs text-slate-500">วาระการดำรงตำแหน่ง 2 ปี (พ.ศ. 2568 - 2570)</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {committeeMembers.map((member, idx) => (
              <div key={idx} className="p-4 rounded-xl border border-slate-200 bg-slate-50/50 space-y-2">
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm">{member.name}</h4>
                    <span className="px-2 py-0.5 rounded text-[10px] font-bold bg-blue-100 text-blue-800">
                      {member.role}
                    </span>
                  </div>
                  <span className="text-[11px] text-slate-400 font-mono">วาระ {member.term}</span>
                </div>

                <div className="text-xs text-slate-600 space-y-1 pt-1 font-mono">
                  <div className="flex items-center gap-1.5">
                    <MapPin className="w-3.5 h-3.5 text-slate-400" />
                    <span>บ้านเลขที่: {member.house}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-blue-700">
                    <Phone className="w-3.5 h-3.5" />
                    <span>เบอร์ติดต่อ: {member.phone}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {activeSubTab === 'bylaws' && (
        <div className="bg-white p-6 rounded-2xl border border-slate-200 shadow-xs space-y-4 text-xs">
          <h3 className="font-bold text-slate-900 text-base">สาระสำคัญของข้อบังคับนิติบุคคล</h3>
          
          <div className="space-y-3">
            {[
              { title: 'หมวดที่ 1: การใช้ทรัพย์ส่วนกลาง', desc: 'ลูกบ้านมีสิทธิใช้สโมสร สระว่ายน้ำ และฟิตเนส ระหว่างเวลา 06:00 - 21:00 น. โดยห้ามส่งเสียงดังรบกวนผู้อื่น' },
              { title: 'หมวดที่ 2: การจอดรถและการจราจร', desc: 'จำกัดความเร็วไม่เกิน 30 กม./ชม. ภายในโครงการ ห้ามจอดรถขวางหน้าบ้านผู้อื่นหรือแนวถนนเมน' },
              { title: 'หมวดที่ 3: สัตว์เลี้ยง', desc: 'ต้องลงทะเบียนสัตว์เลี้ยงกับนิติบุคคล ใช้สายจูงทุกครั้งที่พาเดินนอกรั้วบ้าน และเก็บสิ่งปฏิกูลทันที' },
              { title: 'หมวดที่ 4: การต่อเติมอาคาร', desc: 'ต้องยื่นแบบแปลนและวางเงินประกันความเสียหาย 10,000 บาท อนุญาตให้ช่างทำงานวันจันทร์-เสาร์ เวลา 08:00 - 17:00 น.' },
            ].map((rule, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-xl border border-slate-200 space-y-1">
                <h4 className="font-bold text-slate-800">{rule.title}</h4>
                <p className="text-slate-600 leading-relaxed">{rule.desc}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
